import React, { useRef, useState, useCallback, useEffect } from 'react';
import { motion } from 'framer-motion';
import { ArrowLeft, Link as LinkIcon, Search, List, LibraryBig, Settings2 } from 'lucide-react';
import ThemeToggle from './ThemeToggle';
import AiSidebar from './AiSidebar';
import MindMapCanvas from './MindMapCanvas';
import SummaryModal from './SummaryModal';
import SettingsModal from './SettingsModal';
import DocumentLibrary from './DocumentLibrary';
import PlanMembersModal from './PlanMembersModal';
import PlanSettingsModal from './PlanSettingsModal';
import useIsMobile from '../hooks/useIsMobile';
import { API, useAuth } from '../App';

export const DEPTH_COLORS = [
  '#6366f1','#8b5cf6','#06b6d4','#10b981','#f59e0b','#ef4444','#ec4899',
];
export const depthColor = (d) => DEPTH_COLORS[Math.min(d, DEPTH_COLORS.length - 1)];

export default function CanvasView({ plan, token, isOwner, onPlanUpdated, initialBoxId, onBack, themePref, onThemeChange, shareToken, shareRootId }) {
  const isMobile = useIsMobile();
  const aiSidebarRef = useRef(null);
  const { user } = useAuth();
  const color = plan.color || DEPTH_COLORS[0];
  const sharedAiRestricted = plan.role === 'editor' && plan.has_private_cards;
  const [currentParentId, setCurrentParentId] = useState('root');
  const currentParentIdRef = useRef('root'); // always-current ref for closures
  const [navStack, setNavStack] = useState([]); // [{id, title}] — mirrors MindMapCanvas levelStack
  const [copied, setCopied] = useState(false);
  const [summaryOpen, setSummaryOpen] = useState(false);
  const [summaryStatus, setSummaryStatus] = useState('idle'); // idle | updating | saved
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [libraryOpen, setLibraryOpen] = useState(false);
  const [membersOpen, setMembersOpen] = useState(false);
  const [planSettingsOpen, setPlanSettingsOpen] = useState(false);
  const clientIdRef = useRef(window.crypto.randomUUID());
  const [onlinePeople, setOnlinePeople] = useState([]);
  const [cardLocks, setCardLocks] = useState({});
  const [searchOpen, setSearchOpen] = useState(false);
  const searchButtonRef = useRef(null);
  const [showAiWelcome, setShowAiWelcome] = useState(false);
  const [showAiStarterPrompt, setShowAiStarterPrompt] = useState(false);
  const viewPreferenceKey = `ic_compact_cards_${user?.id || 'guest'}`;
  const [compactView, setCompactView] = useState(() => localStorage.getItem(viewPreferenceKey) === 'true');

  useEffect(() => {
    if (!isOwner || !token) return;
    let active = true;
    const endpoint = `${API}/api/plans/${plan.id}/presence`;
    const headers = { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` };
    const leave = () => fetch(`${endpoint}/${clientIdRef.current}`, { method: 'DELETE', headers, keepalive: true }).catch(() => {});
    const heartbeat = async () => {
      if (document.visibilityState === 'hidden') return;
      try {
        const response = await fetch(endpoint, { method: 'POST', headers, body: JSON.stringify({ clientId: clientIdRef.current }) });
        if (!response.ok || !active) return;
        const data = await response.json();
        if (active) { setOnlinePeople(data.people || []); setCardLocks(Object.fromEntries((data.locks || []).map(lock => [lock.box_id, lock.locked_by]))); }
      } catch {}
    };
    const onVisibility = () => { if (document.visibilityState === 'hidden') { leave(); setOnlinePeople([]); } else heartbeat(); };
    heartbeat();
    const interval = setInterval(heartbeat, 8000);
    document.addEventListener('visibilitychange', onVisibility);
    window.addEventListener('pagehide', leave);
    return () => { active = false; clearInterval(interval); document.removeEventListener('visibilitychange', onVisibility); window.removeEventListener('pagehide', leave); leave(); };
  }, [isOwner, token, plan.id]);

  function toggleCompactView() {
    setCompactView(previous => {
      localStorage.setItem(viewPreferenceKey, String(!previous));
      return !previous;
    });
  }

  // Show the guide once per account, when its first empty plan is opened.
  useEffect(() => {
    if (!isOwner || !token || !user?.id || initialBoxId) return;
    let cancelled = false;
    fetch(`${API}/api/plans`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.ok ? r.json() : null)
      .then(plans => {
        if (cancelled || !Array.isArray(plans)) return;
        if (plans.some(p => p.id === plan.id) && plans.every(p => p.rootBoxCount === 0)) {
          setShowAiStarterPrompt(true);
          const key = `ic_ai_welcome_${user.id}`;
          if (localStorage.getItem(key)) return;
          localStorage.setItem(key, '1');
          setShowAiWelcome(true);
        }
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [isOwner, token, user?.id, initialBoxId, plan.id]);

  // Poll summary status so toolbar indicator stays live
  React.useEffect(() => {
    if (!token || sharedAiRestricted) return;
    let timer;
    async function poll() {
      try {
        const r = await fetch(`/api/plans/${plan.id}/summary`, { headers: { Authorization: `Bearer ${token}` } });
        const d = await r.json();
        setSummaryStatus(d.status === 'updating' ? 'updating' : 'idle');
      } catch {}
      timer = setTimeout(poll, summaryStatus === 'updating' ? 1500 : 8000);
    }
    poll();
    return () => clearTimeout(timer);
  }, [plan.id, token, summaryStatus, sharedAiRestricted]);

  const onLevelChange = useCallback((parentBox, levelStack) => {
    const newId = parentBox ? parentBox.id : 'root';
    setCurrentParentId(newId);
    currentParentIdRef.current = newId;
    // Build navStack from levelStack for AI breadcrumb context
    setNavStack(levelStack ? levelStack.map(l => ({ id: l.box.id, title: l.box.title })) : []);
  }, []);

  async function copyShareUrl() {
    if (isOwner) {
      // Create a scoped share token for the current level
      try {
        const res = await fetch('/api/shares', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
          body: JSON.stringify({ planId: plan.id, rootBoxId: currentParentId !== 'root' ? currentParentId : null }),
        });
        if (!res.ok) throw new Error('Could not create share link');
        const { token: shareToken } = await res.json();
        const url = `${window.location.origin}/s/${shareToken}`;
        navigator.clipboard.writeText(url).then(() => {
          setCopied(true);
          setTimeout(() => setCopied(false), 2000);
        });
        return;
      } catch {}
    }
  }

  // Auth headers for write operations
  const authHeaders = token
    ? { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` }
    : { 'Content-Type': 'application/json' };

  return (
    <div style={{
      width: '100vw', height: '100vh',
      display: 'flex', flexDirection: 'column',
      background: 'var(--bg)', overflow: 'hidden',
    }}>
      {/* ── Toolbar ─────────────────────────────────────────────────────────── */}
      <div style={{
        height: isMobile ? 48 : 52, flexShrink: 0, zIndex: 50,
        background: 'var(--toolbar-bg)', backdropFilter: 'blur(12px)',
        borderBottom: '1px solid var(--border)',
        boxShadow: `0 1px 0 ${color}22`,
        display: 'flex', alignItems: 'center',
        padding: isMobile ? '0 10px' : '0 16px',
        gap: isMobile ? 6 : 10,
      }}>
        {/* Back button — only show if owner (has plans list to go back to) */}
        {isOwner && (
          <motion.button whileHover={{ scale: 1.04 }} whileTap={{ scale: 0.96 }}
            onClick={onBack} style={tbBtn(isMobile)}>
            <ArrowLeft size={13} />
            {!isMobile && ' Plans'}
          </motion.button>
        )}

        {/* Plan title */}
        <div style={{
          flex: 1, fontSize: 14, fontWeight: 700,
          color: 'var(--text-primary)',
          overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
          paddingLeft: isOwner ? 4 : 0,
        }}>
          {plan.title}
          {!isOwner && (
            <span style={{
              marginLeft: 10, fontSize: 10, fontWeight: 600,
              color: 'var(--text-muted)', background: 'var(--bg-4)',
              border: '1px solid var(--border)', borderRadius: 6,
              padding: '2px 7px', verticalAlign: 'middle',
            }}>View only</span>
          )}
        </div>

        {/* Owner plan settings */}
        {plan.role === 'owner' && <motion.button whileHover={{ scale: 1.04 }} whileTap={{ scale: 0.96 }} onClick={() => setPlanSettingsOpen(true)} title="Plan settings" aria-label="Plan settings" style={tbBtn(isMobile)}><Settings2 size={14} />{!isMobile && <span style={{ marginLeft: 4 }}>Plan settings</span>}</motion.button>}

        {/* Reusable plan documents */}
        {isOwner && onlinePeople.length > 0 && <div aria-label={`${onlinePeople.length} other collaborator${onlinePeople.length === 1 ? '' : 's'} viewing this plan`} style={{ display: 'flex', alignItems: 'center', marginRight: 2 }}>
          {onlinePeople.slice(0, 3).map(person => <span key={person.user_id} title={`${person.email} is viewing this plan`} style={{ width: 27, height: 27, borderRadius: '50%', background: '#8b5cf6', color: '#fff', border: '2px solid var(--toolbar-bg)', display: 'grid', placeItems: 'center', fontSize: 10, fontWeight: 700, marginLeft: -5 }}>{person.email.slice(0, 2).toUpperCase()}</span>)}
          {onlinePeople.length > 3 && <span title={onlinePeople.slice(3).map(person => person.email).join(', ')} style={{ fontSize: 11, color: 'var(--text-muted)', marginLeft: 3 }}>+{onlinePeople.length - 3}</span>}
        </div>}
        {isOwner && <motion.button whileHover={{ scale: 1.04 }} whileTap={{ scale: 0.96 }} onClick={() => setLibraryOpen(true)} title="Plan library" aria-label="Plan library" style={tbBtn(isMobile)}><LibraryBig size={14} />{!isMobile && <span style={{ marginLeft: 4 }}>Library</span>}</motion.button>}

        {/* One sharing control: owners can also manage collaborators. */}
        {isOwner && <motion.button
          whileHover={{ scale: 1.04 }} whileTap={{ scale: 0.96 }}
          onClick={() => setMembersOpen(true)}
          title="Share this plan"
          style={{ ...tbBtn(isMobile), color: copied ? '#10b981' : 'var(--text-primary)' }}
        >
          <LinkIcon size={14} />
          {!isMobile && <span style={{ marginLeft: 4 }}>Share</span>}
        </motion.button>}

        <motion.button
          ref={searchButtonRef}
          whileHover={{ scale: 1.04 }} whileTap={{ scale: 0.96 }}
          onClick={() => setSearchOpen(true)}
          title="Search cards (⌘/Ctrl+K)"
          aria-label="Search cards"
          aria-keyshortcuts="Meta+K Control+K"
          style={tbBtn(isMobile)}
        >
          <Search size={14} />
          {!isMobile && <span style={{ marginLeft: 4 }}>Search</span>}
        </motion.button>

        <motion.button
          whileHover={{ scale: 1.04 }} whileTap={{ scale: 0.96 }}
          onClick={toggleCompactView}
          title={compactView ? 'Switch to full card view' : 'Switch to compact card view'}
          aria-label="Compact card view"
          aria-pressed={compactView}
          style={{ ...tbBtn(isMobile), color: compactView ? color : 'var(--text-primary)',
            background: compactView ? `${color}18` : 'var(--bg-4)',
            borderColor: compactView ? `${color}55` : 'var(--border)' }}
        >
          <List size={14} />
          {!isMobile && <span style={{ marginLeft: 4 }}>Compact</span>}
        </motion.button>

        <ThemeToggle value={themePref} onChange={onThemeChange} />
      </div>

      {/* ── Main row: mind map + sidebar ────────────────────────────────────── */}
      <div style={{ display: 'flex', flex: 1, minHeight: 0, overflow: 'hidden', position: 'relative' }}>
        <div style={{ flex: 1, position: 'relative', overflow: 'hidden' }}>
          <MindMapCanvas
            plan={plan}
            token={token}
            clientId={clientIdRef.current}
            cardLocks={cardLocks}
            isOwner={isOwner}
            color={color}
            aiSidebarRef={aiSidebarRef}
            initialFocusId={initialBoxId}
            shareToken={shareToken || null}
            shareRootId={shareRootId || null}
            onLevelChange={onLevelChange}
            searchOpen={searchOpen}
            setSearchOpen={setSearchOpen}
            searchButtonRef={searchButtonRef}
            compactView={compactView}
            onOpenSummary={() => setSummaryOpen(true)}
            summaryStatus={summaryStatus}
          />

        </div>

        {/* AI Sidebar — owner only */}
        {isOwner && !sharedAiRestricted && (
          <AiSidebar
            ref={aiSidebarRef}
            plan={plan}
            navStack={navStack}
            currentParent={currentParentId || 'root'}
            onCreateBox={async (data) => {
              // Read from ref — never stale, even inside a streaming closure
              const pid = currentParentIdRef.current;
              await fetch(`${API}/api/plans/${plan.id}/boxes`, {
                method: 'POST',
                headers: authHeaders,
                body: JSON.stringify({ ...data, parent_id: pid === 'root' ? null : (pid || null) }),
              });
              // Do NOT call _onCardCreated here — AiSidebar calls it once after all cards are done
            }}
            onOpenSettings={() => setSettingsOpen(true)}
            isMobile={isMobile}
            openOnWelcome={showAiWelcome}
            showStarterPrompt={showAiStarterPrompt}
          />
        )}
        {sharedAiRestricted && <div role="status" style={{ width: 240, padding: 16, color: 'var(--text-muted)', fontSize: 12, lineHeight: 1.5, borderLeft: '1px solid var(--border)' }}>
          AI chat and plan summary are owner-only while this plan contains private cards.
        </div>}
      </div>

      {/* Plan document library */}
      {libraryOpen && <DocumentLibrary planId={plan.id} parentId={currentParentId} token={token} onClose={() => setLibraryOpen(false)} />}
      {membersOpen && <PlanMembersModal plan={plan} token={token} onCopyReadOnly={copyShareUrl} copied={copied} onClose={() => setMembersOpen(false)} />}
      {planSettingsOpen && <PlanSettingsModal plan={plan} token={token} onPlanUpdated={onPlanUpdated} onClose={() => setPlanSettingsOpen(false)} />}

      {/* Summary modal */}
      {summaryOpen && (
        <SummaryModal
          plan={plan}
          token={token}
          color={color}
          onClose={() => setSummaryOpen(false)}
        />
      )}

      {/* Settings modal */}
      {settingsOpen && (
        <SettingsModal onClose={() => setSettingsOpen(false)} />
      )}
    </div>
  );
}

const tbBtn = (isMobile) => ({
  background: 'var(--bg-4)', border: '1px solid var(--border)',
  borderRadius: 8, padding: isMobile ? '5px 8px' : '5px 10px',
  color: 'var(--text-primary)', cursor: 'pointer',
  display: 'flex', alignItems: 'center', gap: 4,
  fontSize: 12, fontWeight: 500, fontFamily: 'inherit', flexShrink: 0,
});
